import { AttachMoney, EditCalendar, WhatsApp } from "@mui/icons-material";
import { alpha, Box, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { ConsultationSituationKey } from "../types/consultationsBilling.types";

type ActionKeywordVariant = "whatsapp" | "reschedule" | "charge";

const ACTION_CHIP_COLORS: Record<ActionKeywordVariant, string> = {
  whatsapp: "#25D366",
  reschedule: "#ED6C02",
  charge: "#007C6D",
};

function ActionKeyword({
  icon,
  label,
  variant,
}: {
  icon: ReactNode;
  label: string;
  variant: ActionKeywordVariant;
}) {
  const color = ACTION_CHIP_COLORS[variant];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mx: 0.25,
        px: 0.85,
        py: 0.25,
        borderRadius: 1,
        border: "1px solid",
        borderColor: alpha(color, 0.4),
        bgcolor: alpha(color, 0.12),
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="span"
        sx={{ display: "inline-flex", color, "& svg": { fontSize: 17 } }}
      >
        {icon}
      </Box>
      <Typography
        component="span"
        variant="body2"
        fontWeight={700}
        sx={{ fontSize: "0.9rem", color: "text.primary", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const guideTextSx = {
  m: 0,
  lineHeight: 1.65,
  fontSize: "0.95rem",
  color: "text.secondary",
};

function SituationActionGuide({ situationKey }: { situationKey: ConsultationSituationKey }) {
  const reschedule = (
    <ActionKeyword icon={<EditCalendar />} label="Reagendar" variant="reschedule" />
  );
  const whatsApp = (
    <ActionKeyword icon={<WhatsApp />} label="WhatsApp" variant="whatsapp" />
  );
  const charge = (
    <ActionKeyword icon={<AttachMoney />} label="Cobrar" variant="charge" />
  );

  switch (situationKey) {
    case "overdueBooked":
      return (
        <Typography component="p" variant="body2" sx={guideTextSx}>
          A data passou e o paciente não compareceu. {reschedule} uma nova data ou
          mande {whatsApp} para conversar com ele.
        </Typography>
      );
    case "closed":
      return (
        <Typography component="p" variant="body2" sx={guideTextSx}>
          Consultas que não deram certo. Mande {whatsApp} para trazer o paciente de
          volta.
        </Typography>
      );
    case "booked":
      return (
        <Typography component="p" variant="body2" sx={guideTextSx}>
          Consultas que ainda vão acontecer. {reschedule} se precisar, {charge} quem
          ainda não pagou ou mande {whatsApp} para avisar o paciente.
        </Typography>
      );
    case "fulfilled":
      return (
        <Typography component="p" variant="body2" sx={guideTextSx}>
          Consultas já realizadas. Mande {whatsApp} com orientações ou lembrete de
          retorno.
        </Typography>
      );
    default:
      return null;
  }
}

interface SituationDetailCardProps {
  color: string;
  label: string;
  situationKey: ConsultationSituationKey;
  meta?: string;
  children: ReactNode;
}

export function SituationDetailCard({
  color,
  label,
  situationKey,
  meta,
  children,
}: SituationDetailCardProps) {
  return (
    <Box sx={{ mt: 0.5, width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
        <Box
          sx={{
            width: 3,
            height: 20,
            bgcolor: color,
            borderRadius: 1,
            opacity: 0.45,
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: `${color}45`,
          borderTop: `3px solid ${color}`,
          borderLeft: `4px solid ${color}`,
          borderRadius: 2,
          bgcolor: `${color}07`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: `${color}22`,
            bgcolor: `${color}0c`,
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              sx={{ width: "100%" }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "1.05rem" }}>
                  {label}
                </Typography>
              </Stack>
              {meta ? (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{
                    color,
                    bgcolor: `${color}18`,
                    border: "1px solid",
                    borderColor: `${color}35`,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: 1,
                    whiteSpace: "nowrap",
                    fontSize: "0.9rem",
                  }}
                >
                  {meta}
                </Typography>
              ) : null}
            </Stack>

            <Box
              sx={{
                px: 1.5,
                py: 1.15,
                borderRadius: 1,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: `${color}30`,
                borderLeft: `3px solid ${color}`,
              }}
            >
              <SituationActionGuide situationKey={situationKey} />
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.5 }, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          {children}
        </Box>
      </Paper>
    </Box>
  );
}
